import { useState, useEffect, useMemo, ReactNode } from "react"
import { SelectChangeEvent } from "@mui/material/Select"
import Chips from "./Chips"
import {
   colors,
   getStyledSelect,
   StyledFormControl,
   StyledInputLabel,
   StyledMenuItem,
} from "./styles"
import ArrowDownIcon from "@/assets/icons/ArrowDownIcon"
import { Icon, IconColor, IconProps } from "@/components/shared/Icon"
import { Variant } from "@/types/index"

export interface DropdownOption<T extends string = string> {
   label: string
   value: T
   disabled?: boolean
   selected?: boolean
   ["data-testid"]?: string
}

interface DropdownProps<T extends string = string> {
   size?: Variant
   chipSelection?: boolean
   label?: string
   iconName?: IconProps["name"]
   placeholder?: string
   options?: DropdownOption<T>[]
   disabled?: boolean
   fullWidth?: boolean
   ["data-testid"]?: string
   onSelectionChange?: (selected: T[]) => void
   renderOption?: (option: DropdownOption<T>) => ReactNode
   renderSelectedValue?: (selected: DropdownOption<T>[]) => ReactNode
}

const Dropdown = <T extends string = string>({
   size = Variant.DEFAULT,
   chipSelection = false,
   label,
   iconName,
   placeholder,
   options = [],
   disabled = false,
   fullWidth = true,
   onSelectionChange,
   ["data-testid"]: dataTestId,
   renderOption,
   renderSelectedValue,
}: DropdownProps<T>) => {
   const [selectedValues, setSelectedValues] = useState<T[]>([])

   useEffect(() => {
      const initialSelected = options
         .filter((opt) => opt.selected)
         .map((opt) => opt.value)
      setSelectedValues(initialSelected)
   }, [options])

   const StyledSelect = getStyledSelect(size, chipSelection)

   const parseValue = (value: unknown): T[] => {
      return typeof value === "string"
         ? (value.split(",") as T[])
         : (value as T[])
   }

   const handleChange = (event: SelectChangeEvent<unknown>) => {
      const updatedValues = parseValue(event.target.value)
      setSelectedValues(updatedValues)
      if (onSelectionChange) onSelectionChange(updatedValues)
   }

   const handleDelete = (valueToDelete: string) => {
      const updated = selectedValues.filter((val) => val !== valueToDelete)
      setSelectedValues(updated)
      if (onSelectionChange) onSelectionChange(updated)
   }

   const selectedOptions = useMemo(
      () => options.filter((opt) => selectedValues.includes(opt.value)),
      [options, selectedValues]
   )

   const value = useMemo(() => {
      const hasSelectedOptions =
         options.length > 0 && selectedOptions.length > 0

      if (hasSelectedOptions) {
         return selectedValues
      }

      return chipSelection ? [] : ""
   }, [chipSelection, options.length, selectedOptions.length, selectedValues])

   return (
      <StyledFormControl fullWidth={fullWidth}>
         {!chipSelection && size !== Variant.DEFAULT && (
            <StyledInputLabel
               className="select__label"
               id="select-label"
            >
               {iconName && (
                  <Icon
                     name={iconName}
                     color={IconColor.HONOLULU_BLUE}
                     size="12"
                     style={{ marginRight: 4 }}
                  />
               )}
               {label}
            </StyledInputLabel>
         )}
         {(chipSelection || size === Variant.DEFAULT) &&
            selectedValues.length === 0 && (
               <StyledInputLabel className="select__placeholder">
                  {placeholder}
               </StyledInputLabel>
            )}
         <StyledSelect
            labelId="select-label"
            id="styled-select"
            {...(dataTestId && { "data-testid": dataTestId })}
            disableUnderline
            fullWidth
            name="styled-select"
            multiple={chipSelection}
            value={value}
            disabled={disabled}
            IconComponent={(props) => (
               <ArrowDownIcon
                  {...props}
                  width="8px"
                  height="8px"
               />
            )}
            onChange={handleChange}
            renderValue={
               renderSelectedValue
                  ? () => renderSelectedValue(selectedOptions)
                  : chipSelection
                    ? () => (
                         <Chips
                            items={selectedOptions}
                            onDelete={handleDelete}
                         />
                      )
                    : undefined
            }
            MenuProps={{
               PaperProps: {
                  sx: {
                     backgroundColor: colors.white,
                     mt: 0.5,
                     boxShadow: "none",
                  },
               },
               MenuListProps: {
                  sx: {
                     borderRadius: "4px",
                     border: `1px solid ${colors.grayBorder}`,
                     padding: size === Variant.LARGE ? "12px 16px" : "7px 12px",
                  },
               },
            }}
         >
            {options.map((option) => (
               <StyledMenuItem
                  key={option.value}
                  value={option.value}
                  size={size}
                  chipSelection={chipSelection}
                  disabled={option.disabled}
                  {...(option["data-testid"] && {
                     "data-testid": option["data-testid"],
                  })}
               >
                  {renderOption
                     ? renderOption(option)
                     : chipSelection &&
                       selectedValues.includes(option.value) && (
                          <Icon
                             name="checkmark"
                             size="12"
                             color={IconColor.HONOLULU_BLUE}
                          />
                       )}
                  {!renderOption && option.label}
               </StyledMenuItem>
            ))}
         </StyledSelect>
      </StyledFormControl>
   )
}

export default Dropdown
